import type { ResolvedFigure, ShapeRole } from '../model.js';
import { box, point, SceneBuilder, validateViewport } from '../scene-builder.js';
import { finishPrimitive, type PrimitiveSpec } from './contract.js';
import { d, displaceColumn, fits, type Frame, frameFor, header, note, shellPolygon, TYPE, wrapText } from './kit.js';

export interface AnatomyShell {
  id: string;
  label: string;
  /** Distance from the centre to this shell's outer boundary, in `unit`. */
  outer: number;
  /** A factual clause about what the shell is or does. */
  detail?: string;
  /** Physical state or composition — the anatomical fact, not a style. */
  state?: string;
}

export interface LabelledAnatomySpec extends PrimitiveSpec {
  family: 'labelled-anatomy';
  view: 'section';
  subject: string;
  unit: string;
  /** Ordered inner → outer. Each shell's outer bound is the next shell's inner bound. */
  shells: AnatomyShell[];
  /** What the flat face of the section means. */
  cutNote: string;
  focusShellId?: string;
  title: string;
  subtitle: string;
}

export const anatomyShellId = (id: string): string => `anatomy.shell.${id}`;
export const anatomyCalloutId = (id: string): string => `anatomy.callout.${id}`;
export const ANATOMY_SECTION_ID = 'anatomy.section';
export const ANATOMY_RADIUS_ID = 'anatomy.radius';

interface Metrics {
  frame: Frame;
  laneW: number;
  leaderGutter: number;
  centreX: number;
  centreY: number;
  radius: number;
  calloutTop: number;
  calloutBottom: number;
  lineHeight: number;
  footerY: number;
  required: number;
}

function metrics(spec: LabelledAnatomySpec): Metrics {
  const frame = frameFor(spec.viewport.width, spec.viewport.height);
  const laneW = frame.tier === 'wide' ? 152 : frame.tier === 'phone' ? 134 : 124;
  const leaderGutter = frame.tier === 'compact' ? 22 : 30;
  const centreX = frame.margin + laneW + leaderGutter;
  const footerY = spec.viewport.height - 22;
  // The dimension lane under the disc and the footer's own line count are both
  // reserved before the radius is chosen, so the measure line can never be
  // pushed into the note beneath it.
  const footerLines = wrapText(spec.cutNote.toUpperCase(), TYPE.micro, frame.width - frame.margin * 2, true).length;
  const available = footerY - 44 - (footerLines - 1) * 14 - frame.top;
  const radius = Math.min(frame.width - frame.margin - centreX, available / 2);
  const centreY = frame.top + available / 2;
  return {
    frame,
    laneW,
    leaderGutter,
    centreX,
    centreY,
    radius,
    calloutTop: frame.top + 12,
    calloutBottom: footerY - 26,
    lineHeight: 14,
    footerY,
    required: frame.top + 240 + 40,
  };
}

/**
 * Anatomy is the claim that a part is *there*.
 *
 * A list of components with descriptions is a glossary. A labelled anatomy is a
 * section: every part is drawn where it actually is, at its true extent, and a
 * leader ties its name to that place rather than to a row in a table. This
 * compiler takes measured boundaries, scales them all by one factor, and
 * refuses to give a thin shell a flattering minimum thickness — if a layer is
 * too thin to see at this scale, the figure says so in the shell's own words.
 */
export function compileLabelledAnatomy(spec: LabelledAnatomySpec): ResolvedFigure {
  validateLabelledAnatomy(spec);

  const builder = new SceneBuilder();
  const m = metrics(spec);
  const { frame } = m;
  const warnings: string[] = [];
  const total = spec.shells[spec.shells.length - 1]!.outer;
  const scale = m.radius / total;
  const centre = point(m.centreX, m.centreY);

  header(builder, frame, 'anatomy', `SECTION · ${spec.subject.toUpperCase()}`, [
    `${spec.shells.length} SHELLS · DRAWN AT 1 PX = ${format(1 / scale)} ${spec.unit.toUpperCase()}`,
    `1 PX = ${format(1 / scale)} ${spec.unit.toUpperCase()}`,
  ]);

  // Outer shells first so the inner ones draw over them.
  const ordered = [...spec.shells].reverse();
  for (const shell of ordered) {
    const index = spec.shells.indexOf(shell);
    const inner = index === 0 ? 0 : spec.shells[index - 1]!.outer;
    const outerR = shell.outer * scale;
    const innerR = inner * scale;
    const semanticId = anatomyShellId(shell.id);
    const active = shell.id === spec.focusShellId;
    // Neutral fills graded from the core outward, so the shells separate by
    // material weight rather than by decoration.
    const gradient: ShapeRole[] = ['depth', 'header', 'surface', 'record', 'surface'];
    const role: ShapeRole = active ? 'active' : gradient[Math.min(index, gradient.length - 1)]!;
    builder.polygon(`${semanticId}.shell`, semanticId, role, shellPolygon(centre, outerR, innerR),
      `${shell.label}: ${format(inner)}–${format(shell.outer)} ${spec.unit} from the centre`);
    if (outerR - innerR < 1) {
      warnings.push(`${shell.label} is ${format(shell.outer - inner)} ${spec.unit} thick — under one pixel at this scale, so it is stated in numbers, not drawn to width.`);
    }
  }

  // The cut face: the flat side of the section, hatched the way a cut is.
  builder.line(`${ANATOMY_SECTION_ID}.cut`, ANATOMY_SECTION_ID, 'dimension', point(m.centreX, m.centreY - m.radius), point(m.centreX, m.centreY + m.radius));
  builder.text(`${ANATOMY_SECTION_ID}.cut.label`, ANATOMY_SECTION_ID, 'annotation', point(m.centreX - 9, m.centreY + m.radius - 46), 'CUT FACE', {
    size: TYPE.micro,
    mono: true,
    weight: 700,
    align: 'middle',
    rotate: -90,
    uppercase: true,
  });
  builder.anchor(`${ANATOMY_SECTION_ID}.centre`, ANATOMY_SECTION_ID, centre);
  builder.hit(ANATOMY_SECTION_ID, box(m.centreX, m.centreY - m.radius, m.radius, m.radius * 2));

  // The overall radius, measured from the centre along the cut face.
  const radiusY = m.centreY + m.radius;
  const measureY = radiusY + 26;
  builder.line(`${ANATOMY_RADIUS_ID}.measure`, ANATOMY_RADIUS_ID, 'dimension', point(m.centreX, measureY), point(m.centreX + m.radius, measureY), { markerStart: true, markerEnd: true });
  builder.line(`${ANATOMY_RADIUS_ID}.extension`, ANATOMY_RADIUS_ID, 'construction', point(m.centreX + m.radius, radiusY - 6), point(m.centreX + m.radius, measureY + 4));
  builder.text(`${ANATOMY_RADIUS_ID}.label`, ANATOMY_RADIUS_ID, 'annotation', point(m.centreX + m.radius / 2, measureY - 7), `RADIUS ${format(total)} ${spec.unit}`, {
    size: TYPE.micro,
    mono: true,
    weight: 700,
    align: 'middle',
    uppercase: true,
  });
  builder.anchor(`${ANATOMY_RADIUS_ID}.outer`, ANATOMY_RADIUS_ID, point(m.centreX + m.radius, measureY), 'right');
  builder.hit(ANATOMY_RADIUS_ID, box(m.centreX, measureY - 14, m.radius, 24));

  // Callouts: the true anchor on the cut face is preserved; only the label moves.
  const anchors = spec.shells.map((shell, index) => {
    const inner = index === 0 ? 0 : spec.shells[index - 1]!.outer;
    const mid = ((inner + shell.outer) / 2) * scale;
    return m.centreY - mid;
  });
  const laneText = frame.margin + m.laneW;
  const detailLines = new Map(spec.shells.map((shell) => [shell.id, shell.detail ? wrapText(shell.detail, TYPE.micro, m.laneW, true) : []]));
  const blockHeight = (shell: AnatomyShell): number => (2 + (detailLines.get(shell.id)?.length ?? 0)) * m.lineHeight + 8;
  const blockSpan = Math.max(...spec.shells.map(blockHeight)) + m.lineHeight;
  const placed = displaceColumn(
    anchors.map((y, index) => y - blockHeight(spec.shells[index]!) / 2),
    blockSpan + 6,
    m.calloutTop,
    m.calloutBottom - blockSpan,
  );

  spec.shells.forEach((shell, index) => {
    const semanticId = anatomyCalloutId(shell.id);
    const shellSemantic = anatomyShellId(shell.id);
    const inner = index === 0 ? 0 : spec.shells[index - 1]!.outer;
    const anchorY = anchors[index]!;
    const top = placed[index]!;
    const labelRight = frame.margin + m.laneW;
    const active = shell.id === spec.focusShellId;

    builder.path(`${semanticId}.leader`, semanticId, 'annotation',
      `M ${d(m.centreX - 1)} ${d(anchorY)} L ${d(labelRight + 14)} ${d(anchorY)} L ${d(labelRight + 14)} ${d(top + 12)} L ${d(labelRight + 4)} ${d(top + 12)}`);
    builder.text(`${semanticId}.name`, semanticId, 'structure', point(labelRight, top + 12), shell.label, {
      size: TYPE.label,
      mono: true,
      weight: 700,
      align: 'end',
    });
    builder.text(`${semanticId}.extent`, semanticId, 'annotation', point(labelRight, top + 12 + m.lineHeight), `${format(inner)}–${format(shell.outer)} ${spec.unit}`, {
      size: TYPE.micro,
      mono: true,
      weight: 500,
      align: 'end',
    });
    (detailLines.get(shell.id) ?? []).forEach((line, lineIndex) => {
      builder.text(`${semanticId}.detail.${lineIndex}`, semanticId, 'annotation', point(labelRight, top + 12 + m.lineHeight * (2 + lineIndex)), line, {
        size: TYPE.micro,
        mono: true,
        weight: 500,
        align: 'end',
      });
    });
    // The physical state is written inside its own band, but only where the
    // band is genuinely thick enough to hold it.
    const bandThickness = (shell.outer - inner) * scale;
    const stateText = shell.state ? shell.state.toUpperCase() : '';
    const stateRoom = Math.sqrt(Math.max(0, (shell.outer * scale) ** 2 - (m.centreY - anchorY) ** 2)) - 14;
    if (shell.state && bandThickness >= 26 && fits(stateText, TYPE.micro, stateRoom, true, true)) {
      builder.text(`${shellSemantic}.state`, shellSemantic, 'annotation', point(m.centreX + 10, anchorY + 4), stateText, {
        size: TYPE.micro,
        mono: true,
        weight: 700,
        uppercase: true,
      });
    } else if (shell.state) {
      builder.text(`${semanticId}.state`, semanticId, 'annotation', point(labelRight, top + 12 + m.lineHeight * (2 + (detailLines.get(shell.id)?.length ?? 0))), stateText.toLowerCase(), {
        size: TYPE.micro,
        mono: true,
        weight: 500,
        align: 'end',
      });
    }
    const labelLeft = laneText - m.laneW;
    builder.anchor(`${semanticId}.tip`, semanticId, point(m.centreX, anchorY), 'right');
    builder.anchor(`${semanticId}.label`, semanticId, point(labelRight, top + 12), 'left');
    builder.hit(semanticId, box(Math.max(0, labelLeft - 6), top, labelRight - Math.max(0, labelLeft - 6) + 10, blockHeight(shell)));
    builder.hit(shellSemantic, box(m.centreX, anchorY - 12, Math.min(m.radius, 60), 24));
    builder.relation(
      `${semanticId}.names`,
      'names-part',
      semanticId,
      shellSemantic,
      `${shell.label}${shell.state ? ` (${shell.state})` : ''} occupies ${format(inner)}–${format(shell.outer)} ${spec.unit}`,
    );
    if (index > 0) {
      builder.relation(`${shellSemantic}.encloses`, 'encloses', anatomyShellId(spec.shells[index - 1]!.id), shellSemantic, `${shell.label} lies outside ${spec.shells[index - 1]!.label}`);
    }
    if (active) builder.anchor(`${shellSemantic}.taught`, shellSemantic, point(m.centreX, anchorY), 'right');
  });

  const cutLines = wrapText(spec.cutNote.toUpperCase(), TYPE.micro, frame.contentWidth, true);
  cutLines.forEach((line, index) => {
    note(builder, `anatomy.cut-note.${index}`, 'anatomy', point(frame.margin, m.footerY - (cutLines.length - 1 - index) * 14), line);
  });

  return finishPrimitive(spec, builder, {
    title: spec.title,
    subtitle: spec.subtitle,
    state: spec.focusShellId ? `focus:${spec.focusShellId}` : 'overview',
    active: [spec.focusShellId ? anatomyShellId(spec.focusShellId) : ANATOMY_SECTION_ID, ...(spec.emphasis ?? [])],
    warnings,
  });
}

export function validateLabelledAnatomy(spec: LabelledAnatomySpec): void {
  validateViewport(spec.viewport.width, spec.viewport.height);
  if (spec.family !== 'labelled-anatomy' || spec.view !== 'section') throw new Error('labelled-anatomy: family and view are fixed');
  if (!spec.provenance.trim()) throw new Error('labelled-anatomy: provenance is required');
  if (!spec.subject.trim() || !spec.unit.trim() || !spec.cutNote.trim()) throw new Error('labelled-anatomy: subject, unit and cut note are required');
  if (!spec.title.trim() || !spec.subtitle.trim()) throw new Error('labelled-anatomy: title and subtitle are required');
  if (spec.shells.length < 2 || spec.shells.length > 5) throw new Error('labelled-anatomy: require 2…5 shells');
  const ids = new Set<string>();
  let previous = 0;
  for (const shell of spec.shells) {
    if (!/^[a-z][a-z0-9-]*$/.test(shell.id)) throw new Error(`labelled-anatomy: invalid shell id ${shell.id}`);
    if (ids.has(shell.id)) throw new Error(`labelled-anatomy: duplicate shell id ${shell.id}`);
    ids.add(shell.id);
    if (!shell.label.trim() || shell.label.length > 16) throw new Error(`labelled-anatomy: shell ${shell.id} needs a label of 1…16 characters`);
    if (!Number.isFinite(shell.outer) || shell.outer <= previous) {
      throw new Error(`labelled-anatomy: shell ${shell.id} must extend beyond ${previous}; shells are ordered inner to outer`);
    }
    previous = shell.outer;
  }
  if (spec.focusShellId && !ids.has(spec.focusShellId)) throw new Error('labelled-anatomy: the focus shell does not exist');
  const required = metrics(spec).required;
  if (spec.viewport.height < required) throw new Error(`labelled-anatomy: this section needs at least ${Math.ceil(required)}px of height`);
}

function format(value: number): string {
  if (Number.isInteger(value)) return String(value);
  if (value >= 100) return String(Math.round(value));
  return String(Math.round(value * 10) / 10);
}
